import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import NextLink from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useRouter as useNprogressRouter } from 'next-nprogress-bar'
import { forwardRef, HTMLAttributeAnchorTarget, PropsWithChildren, ComponentProps } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getUpdatedLocaleForURLAfterRegionSwitch as getUpdatedLocaleForURL } from '@src/components/locale/utils'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'

type NextLinkProps = ComponentProps<typeof NextLink>

interface ExtendedLinkProps extends NextLinkProps {
  target?: HTMLAttributeAnchorTarget
  'aria-label'?: string
  tabIndex?: number
}

const UNUSED_LEGACY_PARAM = ''

const LinkI18N = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>(function LinkI18N(
  { onClick, href, target, children, 'aria-label': ariaLabel, ...restProps },
  ref,
) {
  const { t } = useTranslation('common')
  const { locale, regionDetails } = useLocaleContext()
  const updatedLocaleForURL = getUpdatedLocaleForURL(locale, regionDetails)
  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel ?? (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return (
    <NextLink
      {...restProps}
      ref={ref}
      href={href}
      prefetch={false}
      onClick={onClick}
      locale={updatedLocaleForURL || false}
      target={target}
      aria-label={computedAriaLabel}
    >
      {children}
    </NextLink>
  )
})

const LinkWithoutI18n = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>(function LinkWithoutI18n(
  { href, target, children, 'aria-label': ariaLabel, ...restProps },
  ref,
) {
  const { t } = useTranslation('common')
  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel ?? (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return (
    <NextLink ref={ref} href={href} {...restProps} prefetch={false} target={target} aria-label={computedAriaLabel}>
      {children}
    </NextLink>
  )
})

export const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>((props, ref) => {
  const isI18nPreviewEnabled = useI18nPreviewContext()
  return isI18nPreviewEnabled ? <LinkI18N {...props} ref={ref} /> : <LinkWithoutI18n {...props} ref={ref} />
})

// @i18n-ignore
Link.displayName = 'CustomLink'

export const useQuinceNprogressRouter = () => {
  const router = useNprogressRouter()
  const push = (to: string, options?: NavigateOptions) => {
    router.push(to, options)
  }

  const replace = (to: string, options?: NavigateOptions) => {
    router.replace(to, options)
  }

  return {
    ...router,
    push,
    replace,
  }
}

export interface QuinceRouter extends Omit<NextRouter, 'push' | 'replace'> {
  push: (to: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
  replace: (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
  replaceWithHistoryApi: (to: string, windowHistoryState?: unknown) => void
}

export const useQuinceRouter: () => QuinceRouter = () => {
  const router = useRouter()

  const push = (to: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.push(to, to, options)
  }

  const replace = (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.replace(to, as ?? to, options) // as is optional in next/router
  }
  // We're using history API instead of next Router to avoid unnecessary SSR calls. If we use shallow true with next Router, back navigation becomes buggy.
  // More details: https://docs.google.com/document/d/1O6RJi4efrbJfJF_5Viq-F1K1m_LWlYhkSR_qlToAOeE/edit?tab=t.0#heading=h.1y4rii8ztdb0
  const replaceWithHistoryApi = (to: string, windowHistoryState?: unknown) => {
    window.history.replaceState(windowHistoryState, UNUSED_LEGACY_PARAM, to)
  }

  return {
    ...router,
    push,
    replace,
    replaceWithHistoryApi,
  }
}
